import React, { useState, useRef, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

const Flashcard = ({ front, back }) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const backRef = useRef(null);
    const frontRef = useRef(null);
    const [cardHeight, setCardHeight] = useState(256);

    // Measure content to compute dynamic height (min 256px)
    useEffect(() => {
        const measure = () => {
            const frontH = frontRef.current?.scrollHeight || 0;
            const backH = backRef.current?.scrollHeight || 0;
            const needed = Math.max(frontH, backH, 256);
            // Cap at 400px to avoid giant cards, scroll handles the rest
            setCardHeight(Math.min(needed + 40, 400));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [front, back]);

    // Truncate very long back text for display
    const isLongBack = back && back.length > 300;

    return (
        <div
            className="perspective-1000 my-8 w-full max-w-md mx-auto cursor-pointer"
            onClick={() => setIsFlipped(!isFlipped)}
            style={{ height: cardHeight }}
        >
            <motion.div
                className="relative w-full transition-transform duration-500 transform-style-3d"
                style={{ height: cardHeight }}
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
            >
                {/* Front */}
                <div
                    ref={frontRef}
                    className="absolute inset-0 w-full bg-white border-2 border-slate-200 rounded-2xl p-8 flex flex-col items-center justify-center shadow-lg backface-hidden"
                    style={{ height: cardHeight }}
                >
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Flashcard</span>
                    <h3 className="text-2xl font-bold text-slate-800 text-center">{front}</h3>
                    <p className="absolute bottom-4 text-xs text-slate-400 flex items-center gap-1">
                        <RefreshCw size={12} /> Click to flip
                    </p>
                </div>

                {/* Back */}
                <div
                    ref={backRef}
                    className="absolute inset-0 w-full bg-gradient-to-br from-[#0033A0] to-blue-700 text-white rounded-2xl p-6 flex flex-col items-center shadow-xl backface-hidden"
                    style={{ transform: "rotateY(180deg)", height: cardHeight }}
                >
                    <span className="text-xs font-bold text-blue-200 uppercase tracking-widest mb-3 shrink-0">Answer</span>
                    <div className="flex-1 overflow-y-auto w-full overscroll-contain px-2 scrollbar-thin">
                        <p className={`font-medium text-center leading-relaxed whitespace-pre-line ${isLongBack ? 'text-sm' : 'text-lg'}`}>
                            {back}
                        </p>
                    </div>
                    <p className="text-xs text-blue-300 mt-2 shrink-0 flex items-center gap-1">
                        <RefreshCw size={12} /> Click to flip back
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default Flashcard;
